import { ValueChangedEvent } from 'devextreme/ui/number_box';
import { AssignmentDto, AssignmentType, ProjectLoadunitDto } from '../generated/backend';
import { LoadUnitSlots } from '../../project/project-planning/loadUnitSlots';
import { MathHelper } from '../util/math-helper';
import { AssignmentHelper } from '../util/assignment-helper';

export class PlanningLoadunit {
  parent: PlanningLoadunits;
  loadUnitSlots: LoadUnitSlots;
  index: number;
  filled: boolean = false;

  plannedWeight: number = 0;
  plannedVolume: number = 0;

  constructor(lus: LoadUnitSlots, index: number, plus: PlanningLoadunits) {
    this.parent = plus;
    this.loadUnitSlots = lus;
    this.index = index;
  }

  get loadUnitSlot() {
    return this.loadUnitSlots[this.index] ?? null;
  }

  weightCapacity = () => this.loadUnitSlot?.loadUnitContainer.maxLoad ?? 0;
  volumeCapacity = () => this.loadUnitSlot?.loadUnitContainer.maxVolume ?? 0;

  // what is already loaded
  weightLoad = () => this.loadUnitSlot?.cargoLoad.weightTotal(this.parent.assignmentType) ?? 0;
  volumeLoad = () => this.loadUnitSlot?.cargoLoad.volumeTotal(this.parent.assignmentType) ?? 0;

  // percentages for display, using the weight as reference
  occupiedCapacity = () => 100 * (this.weightLoad() / this.weightCapacity());

  plannedCapacity = () => (100 * this.plannedWeight) / this.weightCapacity();

  // overloaded?
  overload = (): boolean => this.volumeLoad() + this.plannedVolume > this.volumeCapacity() + 0.001;


  maxWeight = () => {
    return Math.min(
      this.parent.unplannedWeight() + this.plannedWeight,
      this.weightCapacity() - this.weightLoad()
    );
  };

  maxVolume = () => {
    return this.maxWeight() * this.parent.weightVolumeFactor();
  };
}

// prototype loadunit while planning is ongoing
export class PlanningLoadunits {
  loadUnits: LoadUnitSlots = new LoadUnitSlots(); // { truck?: loadunitSlot; trailer?: loadunitSlot }; // which loadunits are we planning for
  assignment?: AssignmentDto; // the assignment that is to be distributed
  projectLoadunit?: ProjectLoadunitDto; // the projectLoadunit that is to be updated
  assignmentNo!: number; // the assignment number
  assignmentType!: AssignmentType; // the assignment type
  position!: number; // the position in the list
  truck: PlanningLoadunit;
  trailer: PlanningLoadunit;

  // What is to be distributed
  dispoWeight = () => {
    let value = 0;
    if (this.projectLoadunit) {
      if (AssignmentHelper.isDelivery(this.projectLoadunit.Type)) {
        value = this.projectLoadunit.CurrentWeight ?? 0;
      } else {
        value = this.projectLoadunit.CurrentWeight_B ?? 0;
      }
    } else if (this.assignment) {
      const effectiveWeight = AssignmentHelper.effectiveWeight(this.assignment);
      value = (effectiveWeight ?? 0) - (this.assignment.DispoWeight ?? 0);
    }
    return MathHelper.ensureAtLeastZero(value);
  };

  dispoVolume = () => {
    let value = 0;
    if (this.projectLoadunit) {
      if (AssignmentHelper.isDelivery(this.projectLoadunit.Type)) {
        value = this.projectLoadunit.CurrentVolume ?? 0;
      } else {
        value = this.projectLoadunit.CurrentVolume_B ?? 0;
      }
    } else if (this.assignment) {
      const effectiveVolume = AssignmentHelper.effectiveVolume(this.assignment);
      value = (effectiveVolume ?? 0) - (this.assignment.DispoVolume ?? 0);
    }
    return MathHelper.ensureAtLeastZero(value);
  };

  // As weight and volume are not independent, they are connected by a factor
  weightVolumeFactor = () => {
    if (this.dispoVolume() == 0 || this.dispoWeight() == 0) {
      return 1;
    }
    return this.dispoVolume() / this.dispoWeight();
  };

  plannedTotalWeight = () => this.truck.plannedWeight + this.trailer.plannedWeight;
  plannedTotalVolume = () => this.truck.plannedVolume + this.trailer.plannedVolume;
  unplannedWeight = () => MathHelper.ensureAtLeastZero(this.dispoWeight() - this.plannedTotalWeight());
  unplannedVolume = () => MathHelper.ensureAtLeastZero(this.dispoVolume() - this.plannedTotalVolume());
  isFullyAssigned = () => this.unplannedWeight() <= 0 && this.unplannedVolume() <= 0;
  isPartiallyPlanned = () => (this.truck.filled || this.trailer.filled);
  isFullyPlanned = () => (this.truck.filled && this.trailer.filled) || (this.unplannedVolume() <= 0);

  constructor(loadUnits: LoadUnitSlots, position: number) {
    this.loadUnits = loadUnits;
    this.position = position;
    this.truck = new PlanningLoadunit(this.loadUnits, 0, this);
    this.trailer = new PlanningLoadunit(this.loadUnits, 1, this);
    this.projectLoadunit = undefined; // reset the projectLoadunit
    this.assignment = undefined; // reset the assignment

  }

  // set the assignment that is to be planned
  setAssignment(assignment: AssignmentDto) {
    // console.log('setAssignment', assignment);
    this.projectLoadunit = undefined; // reset the projectLoadunit
    this.assignment = assignment;
    this.assignmentNo = assignment.AssignmentNo!;
    this.assignmentType = assignment.Type!;
    return;
  }

  setProjectLoadunit(projectLoadunit: ProjectLoadunitDto, removeCargo: boolean = true) {
    this.assignment = undefined; // reset the assignment
    this.projectLoadunit = projectLoadunit;
    this.assignmentNo = projectLoadunit.AssignmentNo!;
    this.assignmentType = projectLoadunit.Type!;

    const loadingTruck = (this.loadUnits.truck && projectLoadunit.LoadunitId == this.loadUnits.truck?.id);
    const planningLoadunit = loadingTruck ? this.truck : this.trailer;
    const loadUnit = loadingTruck ? this.loadUnits.truck : this.loadUnits.trailer;
    // remove the cargo that matches the projectLoadunits weight and volume
    if (loadUnit) {
      if(removeCargo) {
        loadUnit.cargoLoad.removeItem(projectLoadunit);
      }
      if (AssignmentHelper.isDelivery(projectLoadunit.Type)) {
        planningLoadunit.plannedWeight = projectLoadunit.CurrentWeight ?? 0;
        planningLoadunit.plannedVolume = projectLoadunit.CurrentVolume ?? 0;
      } else {
        planningLoadunit.plannedWeight = projectLoadunit.CurrentWeight_B ?? 0;
        planningLoadunit.plannedVolume = projectLoadunit.CurrentVolume_B ?? 0;
      }
    } else {
      console.error('NO matching loadunit configured in this project');
    }
    console.log('setProjectLoadunit', this.dispoWeight(), this.dispoVolume());
    return;
  }

  updateTruckWeight: (e: ValueChangedEvent) => void = (e: ValueChangedEvent) => {
    if (!e.event) return;
    console.log('updateTruckWeight', e, this.weightVolumeFactor());
    this.truck.plannedWeight = MathHelper.ensureAtLeastZero(e.value / this.weightVolumeFactor());
  };

  updateTruckVolume: (e: ValueChangedEvent) => void = (e: ValueChangedEvent) => {
    if (!e.event) return;
    console.log('updateTruckVolume', e, this.weightVolumeFactor());
    this.truck.plannedVolume = MathHelper.ensureAtLeastZero(e.value * this.weightVolumeFactor());
  };

  updateTrailerWeight: (e: ValueChangedEvent) => void = (e: ValueChangedEvent) => {
    if (!e.event) return;
    console.log('updateTrailerWeight', e, this.weightVolumeFactor());
    this.trailer.plannedWeight = MathHelper.ensureAtLeastZero(e.value / this.weightVolumeFactor());
  };

  updateTrailerVolume: (e: ValueChangedEvent) => void = (e: ValueChangedEvent) => {
    if (!e.event) return;
    console.log('updateTrailerVolume', e, this.weightVolumeFactor());
    this.trailer.plannedVolume = MathHelper.ensureAtLeastZero(e.value * this.weightVolumeFactor());
  };

  fillTruck() {
    this.fill(this.truck);
  }

  fillTrailer() {
    this.fill(this.trailer);
  }

  fillTruck_3DotDialog() {
    this.fill_3DotDialog(this.truck);
  }

  fillTrailer_3DotDialog() {
    this.fill_3DotDialog(this.trailer);
  }

  // Allocate as much volume as possible into the loading unit without exceeding its maximum volume and maximum weight. The weight is allocated proportionally to the volume.
  private fill(planningLoadunit: PlanningLoadunit) {
    if (planningLoadunit && !planningLoadunit.filled) {
      // this does not take into account if the truck is overloaded
      const maxVolumeRemaining = planningLoadunit.volumeCapacity() - planningLoadunit.volumeLoad()
      const maxWeightRemaining = planningLoadunit.weightCapacity() - planningLoadunit.weightLoad();

      let plannedVolume = this.unplannedVolume() <= maxVolumeRemaining ? this.unplannedVolume() : maxVolumeRemaining;
      let plannedWeight = this.unplannedWeight() * (plannedVolume / this.unplannedVolume());

      // Is the maximum weight of the load unit exceeded
      if (plannedWeight > maxWeightRemaining) {
        plannedVolume = this.unplannedVolume() * (maxWeightRemaining / this.unplannedWeight());
        plannedWeight = maxWeightRemaining; // immer????
      }

      planningLoadunit.plannedVolume = MathHelper.ceilTo4(plannedVolume);
      planningLoadunit.plannedWeight = MathHelper.ensureAtLeastZero(MathHelper.ceilTo4(plannedWeight));
      planningLoadunit.filled = true;
    }
  }

  private fill_3DotDialog(planningLoadunit: PlanningLoadunit) {
    if (planningLoadunit) {

      // this does not take into account if the truck is overloaded
      const maxVolumeRemaining = planningLoadunit.volumeCapacity() - planningLoadunit.volumeLoad()
      const maxWeightRemaining = planningLoadunit.weightCapacity() - planningLoadunit.weightLoad();

      const totalVolumeAvailableForDistribution = this.unplannedVolume() + planningLoadunit.plannedVolume; // currently unplanned volume + temporarily selected volume
      const totalWeightAvailableForDistribution = this.unplannedWeight() + planningLoadunit.plannedWeight; // currently unplanned volume + temporarily selected volume

      let plannedVolume = totalVolumeAvailableForDistribution <= maxVolumeRemaining ? totalVolumeAvailableForDistribution : maxVolumeRemaining;
      let plannedWeight = totalWeightAvailableForDistribution * (plannedVolume / totalVolumeAvailableForDistribution);

      // Is the maximum weight of the load unit exceeded
      if (plannedWeight > maxWeightRemaining) {
        plannedVolume = this.unplannedVolume() * (maxWeightRemaining / this.unplannedWeight());
        plannedWeight = maxWeightRemaining; // immer????
      }

      planningLoadunit.plannedVolume = MathHelper.ceilTo4(plannedVolume);
      planningLoadunit.plannedWeight = MathHelper.ensureAtLeastZero(MathHelper.ceilTo4(plannedWeight));
      planningLoadunit.filled = true;
    }
  }
  }

